import { type CSSProperties, type JSX } from 'react';

import { cn } from '@/lib/utils';

export interface ProgressProps {
  /** Numerator. Clamped to [0, total]. */
  value: number;
  /** Denominator. When 0 or missing the bar renders empty. */
  total: number;
  /** Bar height in px. The handoff uses 6 inside dense table rows. */
  height?: number;
  /** Bar fill color. Defaults to volt-500 (success-equivalent on light). */
  color?: string;
  /** Track color. Defaults to surface-3. */
  trackColor?: string;
  className?: string;
}

/**
 * Mini progress bar used in dense table cells (campaigns SENT column,
 * campaign detail header, contact import preview). Pill-shaped track
 * with a flush fill — no animations so a long table doesn't shimmer.
 */
export function Progress({
  value,
  total,
  height = 6,
  color = 'var(--volt-500)',
  trackColor = 'var(--surface-3)',
  className,
}: ProgressProps): JSX.Element {
  const safeTotal = total > 0 ? total : 0;
  const ratio = safeTotal === 0 ? 0 : Math.min(1, Math.max(0, value / safeTotal));
  const trackStyle: CSSProperties = {
    height,
    background: trackColor,
  };
  const fillStyle: CSSProperties = {
    width: `${(ratio * 100).toFixed(2)}%`,
    background: color,
  };
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={safeTotal}
      aria-valuenow={value}
      className={cn('w-full rounded-pill overflow-hidden', className)}
      style={trackStyle}
    >
      <div className="h-full rounded-pill" style={fillStyle} />
    </div>
  );
}
